<template>
  <div class="dash-root">
    <vue-element-loading :active="appLoading" spinner="bar-fade-scale" color="#3ea56e" size="128" is-full-screen />
    <v-snackbar v-model="showSnackBar" :color="snackColor" right :timeout="5000">
      <v-layout wrap justify-center>
        <v-flex text-left class="align-self-center">{{ msg }}</v-flex>
        <v-flex text-right>
          <v-btn small :ripple="false" text @click="showSnackBar = false">
            <v-icon style="color: white">mdi-close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-snackbar>

    <div class="dash-page-header">
      <div>
        <p class="dash-page-header__eyebrow">Marketing</p>
        <h1 class="dash-page-header__title">Email Campaigns</h1>
      </div>
      <div class="dash-page-header__actions">
        <v-btn class="btn-submit" @click="openAddDialog" depressed>
          <v-icon left size="20">mdi-plus</v-icon> Create Campaign
        </v-btn>
      </div>
    </div>

    <v-card class="modern-card mt-6" elevation="0">
      <v-data-table :headers="headers" :items="campaigns" :items-per-page="10" class="modern-table" hover>
        <template v-slot:item.create_date="{ item }">
          <span class="font-weight-medium">{{ formatDate(item.create_date) }}</span>
        </template>
        <template v-slot:item.emails="{ item }">
          <v-chip small color="#f1f5f9" text-color="#475569" class="font-weight-medium">
            <v-icon left size="16" color="#64748b">mdi-account-multiple</v-icon>
            {{ item.emails.length }} Recipients
          </v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn icon class="action-btn action-btn-view" @click="viewCampaign(item._id)" title="View Campaign">
            <v-icon small>mdi-eye</v-icon>
          </v-btn>
        </template>
        <template v-slot:no-data>
          <div class="pa-10 text-center">
            <v-icon size="64" color="#cbd5e1">mdi-email-outline</v-icon>
            <div class="text-h6 mt-4" style="color: #64748b;">No campaigns found</div>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Add Campaign Dialog -->
    <v-dialog v-model="addDialog" max-width="800px" persistent content-class="modern-dialog">
      <v-card class="modern-dialog-card">
        <v-card-title class="modern-dialog-header">
          Create New Email Campaign
          <v-spacer></v-spacer>
          <v-btn icon @click="addDialog = false" class="modern-dialog-close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="modern-dialog-body pt-6">
          <v-form ref="addForm">
            <v-text-field
              v-model="newCampaign.title"
              label="Campaign Title"
              outlined
              dense
              required
              :rules="[v => !!v || 'Title is required']"
              placeholder="Enter subject line for the email"
              class="modern-input"
            ></v-text-field>

            <div class="mb-4">
              <label class="v-label theme--light mb-2 d-block font-weight-medium" style="font-size: 14px; color: #475569;">Email Content</label>
              <vue-editor
                v-model="newCampaign.content"
                placeholder="Write your email body here..."
              ></vue-editor>
              <div v-if="!newCampaign.content && submitting" class="v-messages theme--light error--text mt-1">
                <div class="v-messages__wrapper">
                  <div class="v-messages__message">Content is required</div>
                </div>
              </div>
            </div>

            <v-combobox
              v-model="newCampaign.emails"
              label="Email IDs"
              multiple
              chips
              outlined
              clearable
              deletable-chips
              placeholder="Type email and press enter"
              hint="Press enter to add multiple email addresses"
              persistent-hint
              :rules="[v => v.length > 0 || 'At least one email is required']"
              class="modern-input"
            >
              <template v-slot:selection="{ attrs, item, select, selected, index }">
                <v-chip
                  v-bind="attrs"
                  :input-value="selected"
                  close
                  small
                  @click="select"
                  @click:close="removeEmail(index)"
                  color="#dcfce7"
                  text-color="#16a34a"
                  class="font-weight-medium"
                >
                  {{ item }}
                </v-chip>
              </template>
            </v-combobox>
          </v-form>
        </v-card-text>
        <v-card-actions class="modern-dialog-actions">
          <v-spacer></v-spacer>
          <v-btn text class="btn-cancel" @click="addDialog = false">Cancel</v-btn>
          <v-btn
            class="btn-submit"
            @click="submitCampaign"
            :loading="submitting"
            depressed
          >
            Send Campaign
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- View Campaign Dialog -->
    <v-dialog v-model="viewDialog" max-width="800px" content-class="modern-dialog">
      <v-card class="modern-dialog-card" v-if="selectedCampaign">
        <v-card-title class="modern-dialog-header">
          Campaign Details
          <v-spacer></v-spacer>
          <v-btn icon @click="viewDialog = false" class="modern-dialog-close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="modern-dialog-body pt-6">
          <div class="mb-6">
            <div class="text-overline mb-1" style="color: #64748b; letter-spacing: 0.5px; font-family: poppinssemibold, sans-serif;">TITLE</div>
            <div class="text-h6 font-weight-bold" style="color: #1a202c;">{{ selectedCampaign.title }}</div>
          </div>
          
          <v-divider class="mb-6" style="border-color: #eaedf0;"></v-divider>

          <div class="mb-6">
            <div class="text-overline mb-2" style="color: #64748b; letter-spacing: 0.5px; font-family: poppinssemibold, sans-serif;">CONTENT</div>
            <div class="pa-5 rounded-lg ql-editor" style="background: #f8fafc; border: 1px solid #e2e8f0; min-height: 120px; color: #334155;" v-html="selectedCampaign.content">
            </div>
          </div>

          <v-divider class="mb-6" style="border-color: #eaedf0;"></v-divider>

          <div>
            <div class="text-overline mb-3" style="color: #64748b; letter-spacing: 0.5px; font-family: poppinssemibold, sans-serif;">RECIPIENTS ({{ selectedCampaign.emails.length }})</div>
            <div class="d-flex flex-wrap" style="gap: 8px;">
              <v-chip
                v-for="(email, idx) in selectedCampaign.emails"
                :key="idx"
                small
                color="#f1f5f9"
                text-color="#475569"
                class="font-weight-medium"
              >
                {{ email }}
              </v-chip>
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="modern-dialog-actions">
          <v-spacer></v-spacer>
          <v-btn
            class="btn-cancel"
            text
            @click="viewDialog = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import { VueEditor } from "vue2-editor";

export default {
  components: {
    VueEditor,
  },
  data() {
    return {
      appLoading: false,
      submitting: false,
      showSnackBar: false,
      snackColor: "#3ea56e",
      msg: "",
      campaigns: [],
      headers: [
        { text: "Title", value: "title", align: "start" },
        { text: "Recipients", value: "emails", align: "start" },
        { text: "Date Created", value: "create_date", align: "start" },
        { text: "Actions", value: "actions", sortable: false, align: "start" },
      ],
      addDialog: false,
      viewDialog: false,
      newCampaign: {
        title: "",
        content: "",
        emails: [],
      },
      selectedCampaign: null,
    };
  },
  mounted() {
    this.fetchCampaigns();
  },
  methods: {
    fetchCampaigns() {
      this.appLoading = true;
      axios({
        method: "GET",
        url: "/admin/email-campaign/list",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status) {
            this.campaigns = response.data.data;
          } else {
            this.showError(response.data.msg || "Failed to fetch campaigns");
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.showError("Server error occurred while fetching campaigns");
          console.error(err);
        });
    },
    openAddDialog() {
      this.newCampaign = {
        title: "",
        content: "",
        emails: [],
      };
      if (this.$refs.addForm) this.$refs.addForm.resetValidation();
      this.addDialog = true;
    },
    removeEmail(index) {
      this.newCampaign.emails.splice(index, 1);
    },
    submitCampaign() {
      if (this.$refs.addForm.validate()) {
        if (!this.newCampaign.content || this.newCampaign.content === "<p><br></p>") {
          this.submitting = true; // Trigger the error message display
          this.showError("Content is required");
          setTimeout(() => { this.submitting = false; }, 3000);
          return;
        }
        this.submitting = true;
        axios({
          method: "POST",
          url: "/admin/email-campaign/add",
          headers: {
            "x-auth-token": localStorage.getItem("token"),
          },
          data: this.newCampaign,
        })
          .then((response) => {
            this.submitting = false;
            if (response.data.status) {
              this.showSuccess("Campaign created successfully");
              this.addDialog = false;
              this.fetchCampaigns();
            } else {
              this.showError(response.data.msg || "Failed to add campaign");
            }
          })
          .catch((err) => {
            this.submitting = false;
            this.showError("Server error occurred while adding campaign");
            console.error(err);
          });
      }
    },
    viewCampaign(id) {
      this.appLoading = true;
      axios({
        method: "GET",
        url: "/admin/email-campaign/view/" + id,
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status) {
            this.selectedCampaign = response.data.data;
            this.viewDialog = true;
          } else {
            this.showError(response.data.msg || "Failed to fetch campaign details");
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.showError("Server error occurred while fetching campaign details");
          console.error(err);
        });
    },
    showSuccess(message) {
      this.msg = message;
      this.snackColor = "#3ea56e";
      this.showSnackBar = true;
    },
    showError(message) {
      this.msg = message;
      this.snackColor = "#ff5252";
      this.showSnackBar = true;
    },
    formatDate(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
  },
};
</script>

<style scoped>
.whitespace-pre-wrap { white-space: pre-wrap; }
.border { border: 1px solid #e0e0e0; }

.dash-root { font-family: poppinsmedium, Roboto, sans-serif; padding: 12px 12px; }
.dash-page-header { display: flex; align-items: flex-end; justify-content: space-between; margin-bottom: 24px; flex-wrap: wrap; gap: 16px; }
.dash-page-header__eyebrow { font-size: 13px; color: #8a93a4; margin: 0 0 4px; font-family: poppinsmedium, sans-serif; text-transform: uppercase; letter-spacing: 0.5px; }
.dash-page-header__title { font-size: 22px; font-weight: 700; color: #1a202c; margin: 0; font-family: poppinsbold, sans-serif; line-height: 1.2; }
.dash-page-header__actions { display: flex; gap: 12px; }

.modern-card { background: #ffffff; border: 1px solid #eaedf0; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.02) !important; }
.modern-table { border-radius: 0; }
::v-deep .modern-table .v-data-table-header th { font-family: poppinssemibold, sans-serif; font-size: 13px !important; color: #64748b !important; text-transform: uppercase; letter-spacing: 0.5px; background-color: #f8fafc !important; border-bottom: 1px solid #eaedf0 !important; padding: 12px 16px !important; white-space: nowrap !important; vertical-align: middle !important; }
::v-deep .modern-table .v-data-table-header th span { vertical-align: middle; display: inline-flex; align-items: center; }
::v-deep .modern-table tbody td { font-family: poppinsmedium, sans-serif; font-size: 14px; color: #1a202c; border-bottom: 1px solid #eaedf0 !important; padding: 12px 16px !important; }
::v-deep .modern-table tbody tr:hover { background-color: #f8fafc !important; }

.action-btn { width: 36px; height: 36px; border-radius: 8px !important; margin: 0 4px; transition: all 0.2s ease; }
.action-btn-view { background-color: #f0f9ff !important; color: #0284c7 !important; border: 1px solid #bae6fd !important; }
.action-btn-view:hover { background-color: #e0f2fe !important; transform: translateY(-1px); }

/* Dialog Styles */
::v-deep .modern-dialog { border-radius: 16px !important; overflow: hidden; }
.modern-dialog-card { border-radius: 16px !important; }
.modern-dialog-header { background: #f8fafc; border-bottom: 1px solid #eaedf0; padding: 20px 24px !important; font-family: poppinssemibold, sans-serif; font-size: 18px; color: #1e293b; }
.modern-dialog-close { color: #64748b !important; }
.modern-dialog-body { padding: 24px !important; background: #ffffff; }
.modern-dialog-actions { padding: 16px 24px !important; background: #f8fafc; border-top: 1px solid #eaedf0; }
.btn-cancel { color: #64748b !important; font-family: poppinsmedium, sans-serif; text-transform: none; font-size: 14px; letter-spacing: 0; border-radius: 8px; }
.btn-submit { background: #3ea56e !important; color: #ffffff !important; font-family: poppinsmedium, sans-serif; text-transform: none; font-size: 14px; letter-spacing: 0; border-radius: 8px; padding: 0 24px !important; box-shadow: 0 4px 12px rgba(62, 165, 110, 0.2) !important; }
</style>
